import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { shouldHideOllieFab } from './ollieFabPlacement'

const OllieAssistantContext = createContext(null)

export function OllieAssistantProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false)
  const [draftMessage, setDraftMessage] = useState('')
  const [pendingFindingCheckId, setPendingFindingCheckId] = useState(null)
  const [pendingFindingAction, setPendingFindingAction] = useState(null)
  const [pendingFindingAsk, setPendingFindingAsk] = useState(null)
  const [fabSuppressed, setFabSuppressed] = useState(false)

  const blurActiveField = () => {
    if (typeof document === 'undefined') return
    const active = document.activeElement
    if (active && typeof active.blur === 'function') active.blur()
  }

  const openOllie = useCallback(() => {
    blurActiveField()
    setDraftMessage('')
    setIsOpen(true)
  }, [])

  const openOllieWithDraft = useCallback(message => {
    blurActiveField()
    setDraftMessage(String(message || '').trim())
    setIsOpen(true)
  }, [])

  /**
   * Open straight into a finding's thread (feature-page "Why?"). With
   * `startAction` (a prepare / draft action from a noticed row or Ollie's
   * pick) the thread opens with that action's sheet showing; nothing is
   * saved until they confirm in the sheet. With `draft` the question waits
   * in the composer, unsent, so nothing is spent until they tap Send. With
   * `ask: 'why'` (a "Why?" link) the thread asks its Why? chip at once.
   */
  const openOllieWithFinding = useCallback(
    (checkId, { startAction, draft, ask } = {}) => {
      if (!checkId) return
      blurActiveField()
      setDraftMessage(String(draft || '').trim())
      setPendingFindingCheckId(String(checkId))
      setPendingFindingAction(startAction?.id ? startAction : null)
      setPendingFindingAsk(ask === 'why' ? 'why' : null)
      setIsOpen(true)
    },
    [],
  )

  const clearPendingFinding = useCallback(() => {
    setPendingFindingCheckId(null)
    setPendingFindingAction(null)
    setPendingFindingAsk(null)
  }, [])

  const closeOllie = useCallback(() => {
    setIsOpen(false)
    setDraftMessage('')
    setPendingFindingCheckId(null)
    setPendingFindingAction(null)
    setPendingFindingAsk(null)
  }, [])

  const clearDraft = useCallback(() => {
    setDraftMessage('')
  }, [])

  const value = useMemo(
    () => ({
      isOpen,
      draftMessage,
      pendingFindingCheckId,
      pendingFindingAction,
      pendingFindingAsk,
      openOllie,
      openOllieWithDraft,
      openOllieWithFinding,
      clearPendingFinding,
      closeOllie,
      clearDraft,
      setIsOpen,
      fabSuppressed,
      setFabSuppressed,
    }),
    [
      isOpen,
      draftMessage,
      pendingFindingCheckId,
      pendingFindingAction,
      pendingFindingAsk,
      openOllie,
      openOllieWithDraft,
      openOllieWithFinding,
      clearPendingFinding,
      closeOllie,
      clearDraft,
      fabSuppressed,
    ],
  )

  return (
    <OllieAssistantContext.Provider value={value}>
      {children}
    </OllieAssistantContext.Provider>
  )
}

export function useOllieAssistant() {
  const context = useContext(OllieAssistantContext)
  if (!context) {
    return {
      isOpen: false,
      draftMessage: '',
      pendingFindingCheckId: null,
      pendingFindingAction: null,
      pendingFindingAsk: null,
      openOllie: () => {},
      openOllieWithDraft: () => {},
      openOllieWithFinding: () => {},
      clearPendingFinding: () => {},
      closeOllie: () => {},
      clearDraft: () => {},
      setIsOpen: () => {},
      fabSuppressed: false,
      setFabSuppressed: () => {},
    }
  }
  return context
}

/** Hide the closed-assistant FAB while Groups / OTP (or similar) is open. */
export function useSuppressOllieFab({
  groupsOpen = false,
  orderToParOpen = false,
} = {}) {
  const { setFabSuppressed } = useOllieAssistant()
  const hide = shouldHideOllieFab({ groupsOpen, orderToParOpen })
  useEffect(() => {
    setFabSuppressed(hide)
    return () => setFabSuppressed(false)
  }, [hide, setFabSuppressed])
}
